/* global G */
import { setKey } from 'lib/util'
import confirm from 'lib/hook/router/confirm'
import ui from 'lib/hook/ui/update'
import redirect from 'lib/hook/router/redirect'
import readAttribute from 'lib/hook/model/readAttribute'
import resetAttribute from 'lib/hook/model/resetAttribute'
import submitAttribute from 'lib/hook/model/submitAttribute'
import validateAttribute from 'lib/hook/model/validateAttribute'
import create from 'app/_shared/hook/appbar/create'
import check from 'app/_shared/hook/check'
import clear from 'app/_shared/hook/model/clear'
import back from 'app/_shared/hook/redirect/back'
import backHandler from 'app/_shared/hook/appbar/back'
import mapAttribute from 'app/_shared/hook/model/mapAttribute'
import quickActions from 'app/_shared/hook/appbar/quickActions'
import clean from 'app/_shared/hook/action/clean'
import resetComponent from 'app/_shared/hook/component/reset'
import recall from 'app/_shared/hook/component/recall'
import onlineCheck from 'app/_shared/hook/onlineCheck'
import platform from 'app/_shared/hook/platform'
import multiCreate from 'app/_shared/hook/appbar/multiCreate'
import routeComposition from 'trait/composition/route'
import go from 'app/_shared/hook/redirect/go'
import startMeasurement from 'app/xEcoApm/hook/startMeasurement'
import generateReport from 'app/xEcoApm/hook/generateReport'
import setPairingSensorRef from 'app/xEcoApm/hook/setPairingSensorRef'
import cacheMeasurementData from 'app/xEcoApm/hook/cacheMeasurementData'
import loadCachedMeasurementData from 'app/xEcoApm/hook/loadCachedMeasurementData'
import licenceActivate from 'app/xEcoApm/hook/licenceActivate'
import licenceUpdate from 'app/xEcoApm/hook/licenceUpdate'
import { submitAttributeWithDrafts } from 'app/_shared/hook/model/submitWithDrafts'
import { readAttributeWithDrafts } from 'app/_shared/hook/model/readWithDrafts'

const _baseHooks = {
  before: [
    recall,
  ],
  after: [
    ui,
  ],
}

const _indexHooks = {
  before: [
    recall,
  ],
  after: [
    platform({
      mobile: quickActions
    }),
    ui,
  ],
}

const _measurementCreateHooks = {
  before: [
    loadCachedMeasurementData,
    mapAttribute('measurement', 'form'),
    platform({
      mobile: backHandler,
    })
  ],
  after: [
    ui,
    startMeasurement,
  ],
}

const _measurementSubmitHooks = {
  before: [],
  after: [
    ui,
    check,
    submitAttributeWithDrafts('measurement', {
      ns: 'apmGo',
      type: 'measurement'
    }),
    resetAttribute('measurement'),
    clear,
    resetComponent,
    go(-2),
  ],
}

const _measurementDetailHooks = {
  before: [
    readAttributeWithDrafts('measurement'),
  ],
  after: [
    quickActions,
    ui,
  ],
}

const _validateRedirectHooks = {
  before: [
    validateAttribute('measurement', 'form'),
    obj => async (...args) => {
      const model = obj[G.MODEL]
      obj[G.STATE][G.ERROR] = model[G.CHILDREN].measurement[G.STATE][G.ERROR]
      return args
    },
  ],
  after: [
    ui,
    redirect,
  ]
}

const _reportGenerateHooks = {
  before: [
    cacheMeasurementData,
  ],
  after: [
    ui,
    generateReport,
    resetComponent,
  ],
}

const _reportHooks = {
  before: [
    mapAttribute('measurement', 'details'),
    platform({
      mobile: backHandler,
    }),
  ],
  after: [
    quickActions,
    ui,
  ],
}

const _sensorDetailHooks = {
  before: [
    // resetAttribute('measurement'),
    readAttribute('sensor'),
  ],
  after: [
    quickActions,
    ui,
    platform({
      mobile: [
        licenceActivate,
        licenceUpdate,
      ]
    }),
  ],
}

const _sensorUsageHooks = {
  before: [],
  after: [
    ui,
    backHandler,
  ],
}

const _pairingCreateHooks = {
  before: [
    mapAttribute('pairing'),
  ],
  after: [
    quickActions,
    ui,
  ],
}

const _pairingSubmitHooks = {
  before: [
    // validateAttribute('pairing'),
  ],
  after: [
    ui,
    check,
    submitAttribute('pairing'),
    setPairingSensorRef,
    clean,
    clear,
    // resetComponent,
    resetAttribute('pairing'),
    obj => (...args) => {
      // In the index action, we search for existing data to know
      // whether we show the teaser page or the content. However,
      // because we don't wait for the promise here once we hit the
      // new action the data hasn't properly been saved on the server.
      // So we set a flag here to indicate that.
      setKey(true, 'data', obj[G.STATE])

      return args
    },
  ],
}

const _licenceIndexHooks = {
  before: [
    create,
  ],
  after: [
    ui,
  ],
}

const _licenceDetailHooks = {
  before: [
    readAttribute('licence'),
  ],
  after: [
    ui,
  ],
}

const _licenceCreateHooks = {
  before: [
    mapAttribute('licence'),
  ],
  after: [
    ui,
    confirm,
  ],
}

const _licenceSubmitHooks = {
  before: [
    validateAttribute('licence'),
  ],
  after: [
    ui,
    check,
    submitAttribute('licence'),
    // Navigate to newly created object on back
    obj => async (...args) => {
      const [model] = args
      const key = model[G.STATE][G.REF]
      const session = obj[G.ADAPTER][G.SESSION]
      const stack = session[G.STATE][G.PREV]

      stack.push(routeComposition('xEcoApm', 'licenceDetail', key))

      return args
    },
    resetAttribute('licence'),
    clear,
    resetComponent,
    back,
  ],
}

const _listHooks = {
  before: [
    onlineCheck(),
    platform({
      mobile: [multiCreate],
    }),
  ],
  after: [
    ui,
  ],
}

const hooks = {
  [G.ACTIONS]: {
    index: _indexHooks,
    teaser: _baseHooks,

    measurementIndex: _baseHooks,
    measurementCreate: _measurementCreateHooks,
    measurementSubmit: _measurementSubmitHooks,
    measurementDetail: _measurementDetailHooks,

    validateRedirect: _validateRedirectHooks,

    reportGenerate: _reportGenerateHooks,
    report: _reportHooks,

    sensorIndex: _baseHooks,
    sensorDetail: _sensorDetailHooks,
    sensorUsage: _sensorUsageHooks,

    pairingCreate: _pairingCreateHooks,
    pairingSubmit: _pairingSubmitHooks,

    licenceIndex: _licenceIndexHooks,
    licenceDetail: _licenceDetailHooks,
    licenceCreate: _licenceCreateHooks,
    licenceInsert: _baseHooks,
    licenceSubmit: _licenceSubmitHooks,

    serviceItems: _baseHooks,

    connect: _baseHooks,
    contacts: _listHooks,
  },
}

export default hooks
